/*
 * helpers.js
 *
 * Various helper functions
 */

import moment from 'moment';
import React from 'react';
import Flagship from '../core/flagship';
import {Link} from 'react-router';
import FileUploadFieldView from "../components/records/FileUploadFieldView";
import UserDataModal from "../components/modals/UserDataModal";


export function throttle(fn, delay, timer = null) {
    clearTimeout(timer);
    timer = setTimeout(fn, delay);
    return timer;
}


function getLinkedTitle( data, includeKey ) {
    if (!empty(data) && !empty(data.link) && !empty(data.key) && !empty(data.name)) {
        return (
            <Link to={data.link}>
                {(includeKey === true ? '#' + data.key + ' - ' + data.name : data.name)}
            </Link>
        );
    } else if (typeof data === "string"){
        return data;
    } else {
        return "Unknown";
    }
}

export function getFormattedValue(value, format = 'text', i = 0, mode = "view") {

    let formattedValue = value;

    // Format an individual value
    const formatValue = (value, format) => {

        let newValue = value;
        if (format === "date") {
            if (!empty(value)) {
                newValue = moment.unix(newValue).format("MM/DD/YYYY");            }
        } else if (format === "time ago") {
            newValue = moment(value).fromNow();
        } else if (format === "money") {
            if ((typeof newValue !== "string" || newValue.charAt(0) !== "$") && newValue !== "") {
                newValue = "$" + newValue;
            }
        } else if (format === 'client-name') {
            newValue = getLinkedTitle(value, false);
        } else if (format === 'url' && mode === "view") {
            newValue = <span dangerouslySetInnerHTML={{__html: '<a target="_blank" href="' + value + '">' + value + '</a>'}}></span>
        } else if (format === "filesize") {
            let i = value === 0 ? 0 : i = Math.floor( Math.log(newValue) / Math.log(1024) );
            newValue = ( value / Math.pow(1024, i) ).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
        } else if (format === 'rfp-number') {
            newValue = getLinkedTitle(value, true);
        } else if (format === 'io-product') {
            newValue = Flagship.config.lineItemServices.find(function(element) {
                return element.value === value;
            });
            if (!empty(newValue) && !empty(newValue.text)){
                newValue = newValue.text;
            }
        } else if (format === "flight") {
            return (
                <div className="single-flight-view">
                    <span className="flight-label">Flight #{(i+1)}</span>
                    <div className="flight-details">
                        <strong>Dates: </strong> {" " + moment(value.startDate).format('MM/DD/YY')} - {" " + moment(value.endDate).format('MM/DD/YY')}
                        <br/>
                        <strong>Budget: </strong> {" " + value.budget}
                        <div className="flight-goal">
                        <strong> Flight Goal:</strong> {" " + value.goal}
                        {!empty(value.retailCPM) ? <div style={{display: "inline-block"}}>&nbsp; <span>//</span> <strong>Retail CPM:</strong> {value.retailCPM}</div> : null}
                        </div>
                    </div>
                </div>
            );
        } else if (format === "file") {
            return (
                <FileUploadFieldView folder={value} />
            );
        } else if (format === "user" ) {
            if ( empty( value ) ) {
                return null;
            } else if ( typeof value === "number" ) {
                return (
                    <UserDataModal getUser={value} />
                );
            }
            return (
               <UserDataModal {...value} />
            );
        }

        // Convert newlines to br
        if (typeof newValue === "string" && newValue.indexOf('\n') !== -1) {

            return newValue.split('\n').map(function(item, key) {
                return (
                  <span key={key}>
                      {item}
                      <br/>
                  </span>
                );
            });

        }
        return newValue;

    };

    if (Array.isArray(value)) {
        return (
            <div>
                {value.map(function(val) {
                    return (
                        <div>
                        {formatValue(val, format)}
                        </div>
                    );
                })}
            </div>
        );


    } else {
        return formatValue(formattedValue, format);
    }
}

/**
 *
 * Detect if two values are equal (works for arrays and objects)
 * @param a
 * @param b
 * @returns {boolean}
 */
export function isEqual(a, b) {

    if (typeof a !== typeof b || Array.isArray(a) !== Array.isArray(b)) {
        return false;
    } else if (Array.isArray(a)) {

        if (a === b) return true;
        if (a == null || b == null) return false;
        if (a.length !== b.length) return false;

        for (var i = 0; i < a.length; ++i) {
            if (a[i] !== b[i]) return false;
        }
        return true;

    } else if (isObject(a)) {

        let aProps = Object.getOwnPropertyNames(a);
        let bProps = Object.getOwnPropertyNames(b);


        if (aProps.length !== bProps.length) {
            return false;
        }

        for (let i = 0; i < aProps.length; i++) {
            let propName = aProps[i];
            if (a[propName] !== b[propName]) {
                return false;
            }
        }
        return true;

    } else {
        return a === b;
    }
}


/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item) {
    return (item && typeof item === 'object' && !Array.isArray(item));
}

/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 */
export function mergeDeep(target, ...sources) {
    if (!sources.length) return target;
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                mergeDeep(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }

    return mergeDeep(target, ...sources);
}

// Asynchronous version of `setRouteLeaveHook`.
// Instead of synchronously returning a result, the hook is expected to
// return a promise.
export function setAsyncRouteLeaveHook(router, route, hook) {
    let withinHook = false;
    let finalResult = undefined;
    let finalResultSet = false;
    router.setRouteLeaveHook(route, nextLocation => {
        withinHook = true;
        if (!finalResultSet) {
            hook(nextLocation).then(result => {
                finalResult = result;
                finalResultSet = true;
                if (!withinHook && nextLocation) {
                    // Re-schedule the navigation
                    router.push(nextLocation)
                }
            })
        }
        let result = finalResultSet ? finalResult : false;
        withinHook = false;
        finalResult = undefined;
        finalResultSet = false;
        return result
    })
}

// Determine whether or not a field should be shown, given other field data
export function shouldShowField( field, fieldData ) {
    if (!field.hasOwnProperty("where") || field.where === "" || field.where === [] || field.where === null) {
        return true;
    }

    let result = true;

    if (typeof field.where.map !== "function" && typeof field.where === "object") {
        field.where = [field.where];
    }

    if (typeof field.where.map === "function") {
        field.where.map(function (where) {
            if (
                !where.hasOwnProperty("field") ||
                !where.hasOwnProperty("check") ||
                !where.hasOwnProperty("against")
            ) {
                result = false;
                return false;
            } else {

                if ( !empty( fieldData ) && fieldData.hasOwnProperty(where.field) ) {
                    switch (where.check) {

                        case "=":
                            if ([where.field] !== where.against) {
                                result = false;
                            }
                            break;
                        case "!=":
                            if (where.against === "" && typeof fieldData[where.field] === "undefined") {
                                result = false;
                            } else if (fieldData[where.field] === where.against) {
                                result = false;
                            }
                            break;
                        case "in":
                            if (empty(fieldData[where.field]) || !fieldData[where.field].includes(where.against)) {
                                result = false;
                            }
                            break;
                        case "not in":
                            if (!empty(fieldData[where.field]) && fieldData[where.field].includes(where.against)) {
                                result = false;
                            }
                            break;
                    }
                }
            }

        });
    }
    return result;
}

export function empty(variable) {
    return (
        typeof variable === "undefined" ||
        variable === "" ||
        variable === null ||
        variable === [] ||
        Object.keys(variable).length === 0 && variable.constructor === Object);
}

export function renderFieldValue(value, format = '') {
    if( Array.isArray(value)) {

        return value.map(function (val, i) {
            return (
                <div key={"value-" + i}>
                    {getFormattedValue(val, format, i)}
                </div>
            );
        });
    } else {
        return getFormattedValue(value, format);
    }
}

export function clean(obj) {
    for (let propName in obj) {
        if (obj[propName] === null || obj[propName] === undefined) {
            obj[propName] = '';
        }
    }
    return obj;
}

export function getUrlParameter(name) {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    let regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    let results = regex.exec(location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
}


export function randomPassword() {

    let newPassword = '';
    let length = 14;
    let charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    for (let i = 0, n = charset.length; i < length; ++i) {
        newPassword += charset.charAt(Math.floor(Math.random() * n));
    }
    return newPassword;
}

export function arraysEqual(a,b) {
    if (a instanceof Array && b instanceof Array) {
        if (a.length!==b.length)  // assert same length
            return false;
        for(let i=0; i<a.length; i++)  // assert each element equal
            if (!arraysEqual(a[i],b[i]))
                return false;
        return true;
    } else {
        return a===b;  // if not both arrays, should be the same
    }
}

export function uniqueId() {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }
    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
}


// WEBPACK FOOTER //
// ./src/utils/helpers.js